import React, { useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { motion } from 'framer-motion';
import { Plus, Trash2, Loader2 } from 'lucide-react';
import { useUsers } from '@/contexts/UsersContext';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import ConfirmDialog from '../ui/ConfirmDialog';

import { useAuth } from '@/contexts/AuthContext';
import { getTransformedUrl } from '@/utils/supabaseImage';

const SalonGallery: React.FC = () => {
    const { t } = useTranslation();
    const { isAdmin } = useAuth();
    const { gallery, uploadGalleryImage, deleteGalleryImage } = useUsers();

    const readOnly = !isAdmin;

    // State for loading capability per slot (not strictly needed as we append, but good for UX)
    // Actually we only add one at a time.
    const [isUploading, setIsUploading] = useState(false);
    const [deletingId, setDeletingId] = useState<number | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // We always show 4 slots
    const slots = [0, 1, 2, 3];

    const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (!file) return;

        // Reset input value to allow selecting same file again if needed (though unlikely)
        e.target.value = '';

        setIsUploading(true);
        setIsUploading(true);
        try {
            const res = await uploadGalleryImage(file);
            if (res.success) {
                toast.success(t('salon.gallery.upload_success', 'Immagine caricata'));
            } else {
                toast.error(t('salon.gallery.upload_error', 'Errore upload'));
            }
        } catch (error: any) {
            console.error(error);
            toast.error(error.message || t('common.error'));
        } finally {
            setIsUploading(false);
        }
    };

    const handleDelete = async () => {
        if (!deletingId) return;
        try {
            const res = await deleteGalleryImage(deletingId);
            if (res.success) {
                toast.success(t('salon.gallery.delete_success', 'Immagine rimossa'));
            } else {
                toast.error(t('common.error'));
            }
        } catch (error) {
            toast.error(t('common.error'));
        } finally {
            setDeletingId(null);
        }
    };

    return (
        <motion.div
            variants={{ hidden: { opacity: 0, y: 10 }, visible: { opacity: 1, y: 0 } }}
            className="bg-white p-8 rounded-3xl border border-gray-100 shadow-sm"
        >
            <div className="flex justify-between items-center mb-6">
                <div>
                    <h3 className="text-xl font-bold text-gray-900">{t('salon.gallery.title', 'Galleria')}</h3>
                    <p className="text-gray-500 text-sm">{t('salon.gallery.subtitle', 'Mostra fino a 4 immagini del tuo salone')}</p>
                </div>
                <div className="text-sm font-medium text-gray-500">
                    {gallery.length}/4
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                {slots.map((index) => {
                    const item = gallery[index];
                    const isOccupied = !!item;

                    return (
                        <div
                            key={index}
                            className="aspect-square rounded-2xl overflow-hidden relative group bg-gray-50 border-2 border-dashed border-gray-200 flex items-center justify-center transition-all hover:border-clip-300"
                        >
                            {isOccupied ? (
                                <>
                                    <img
                                        src={getTransformedUrl(item.image_url, 'gallery')}
                                        alt="Gallery item"
                                        className="w-full h-full object-cover"
                                    />
                                    {!readOnly && (
                                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                            <button
                                                onClick={() => setDeletingId(item.id)}
                                                className="bg-white text-red-500 p-2 rounded-xl hover:bg-red-50 transition-colors"
                                                title={t('common.delete')}
                                            >
                                                <Trash2 size={18} />
                                            </button>
                                        </div>
                                    )}
                                </>
                            ) : (
                                <button
                                    onClick={() => fileInputRef.current?.click()}
                                    disabled={readOnly || isUploading || gallery.length >= 4}
                                    className={`w-full h-full flex flex-col items-center justify-center gap-2 transition-all
                                        ${readOnly
                                            ? 'text-gray-300 bg-gray-50/50 cursor-default'
                                            : 'text-gray-400 hover:text-clip-500 hover:bg-clip-50/50 cursor-pointer disabled:opacity-50 disabled:hover:text-gray-400 disabled:hover:bg-transparent disabled:cursor-not-allowed'
                                        }`}
                                >
                                    {isUploading && index === gallery.length ? (
                                        <Loader2 className="animate-spin" size={24} />
                                    ) : (
                                        <>
                                            {!readOnly && <Plus size={24} />}
                                            {!readOnly && <span className="text-xs font-semibold">{t('common.add', 'Aggiungi')}</span>}
                                        </>
                                    )}
                                </button>
                            )}
                        </div>
                    );
                })}
            </div>

            <input
                type="file"
                ref={fileInputRef}
                className="hidden"
                accept="image/*"
                onChange={handleFileSelect}
            />

            <ConfirmDialog
                isOpen={!!deletingId}
                title={t('salon.gallery.delete_title', 'Elimina immagine')}
                description={t('salon.gallery.delete_confirm', 'Sei sicuro di voler eliminare questa immagine?')}
                confirmLabel={t('common.delete')}
                onConfirm={handleDelete}
                onCancel={() => setDeletingId(null)}
            />

            {/* Modal Overlay per Caricamento tramite Portal per coprire l'intero schermo */}
            {isUploading && createPortal(
                <div className="fixed inset-0 z-[100000] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center">
                    <Loader2 className="w-12 h-12 text-white animate-spin mb-4" />
                    <p className="text-white font-medium text-lg">{t('common.uploading', 'Caricamento in corso...')}</p>
                </div>,
                document.body
            )}
        </motion.div>
    );
};

export default SalonGallery;
